import React from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, ArrowLeft } from 'lucide-react';
import styles from './PageHeader.module.scss';

interface PageHeaderProps {
    /** Page title displayed in center */
    title: string;
    /** Callback for back navigation - shows back arrow on left when provided */
    onBack?: () => void;
    /** Callback for menu button click */
    onMenuClick?: () => void;
    /** Optional action button on right side (e.g., "Today" button) */
    rightAction?: React.ReactNode;
    /** 
     * @deprecated Use onMenuClick instead. Legacy support for menu button as children.
     * When onMenuClick is provided, children are ignored for the left slot.
     */
    children?: React.ReactNode;
    /** Make header transparent (for overlay on camera, etc.) */
    transparent?: boolean;
    /** Additional CSS class for header wrapper */
    className?: string;
}

/**
 * Unified PageHeader component for consistent navigation across all pages.
 * 
 * Layout patterns:
 * - Main pages: [Menu☰] [Title] [RightAction?]
 * - Sub-pages:  [←Back] [Title] [Menu☰]
 * 
 * The menu button is always accessible - on the left for main pages,
 * on the right for sub-pages (when back navigation is shown).
 */
const PageHeader: React.FC<PageHeaderProps> = ({ 
    title, 
    onBack, 
    onMenuClick,
    rightAction,
    children, 
    transparent = false,
    className = ''
}) => {
    const { t } = useTranslation();

    // Determine if we should render the internal menu button
    // Legacy support: if children provided and no onMenuClick, use children
    const hasInternalMenuHandler = !!onMenuClick;
    const showLegacyChildren = !hasInternalMenuHandler && !!children;

    const renderMenuButton = () => (
        <button
            className={styles.iconButton}
            onClick={onMenuClick}
            aria-label={t('a11y_open_menu')}
        >
            <Menu size={24} aria-hidden="true" />
        </button>
    );

    const renderBackButton = () => (
        <button 
            className={styles.iconButton} 
            onClick={onBack}
            aria-label={t('a11y_go_back')}
        >
            <ArrowLeft size={24} aria-hidden="true" />
        </button>
    );

    const headerClasses = [
        styles.header,
        transparent ? styles.headerTransparent : '',
        className
    ].filter(Boolean).join(' ');

    const contentClasses = [
        styles.headerContent,
        transparent ? styles.headerContentTransparent : ''
    ].filter(Boolean).join(' ');

    return (
        <header className={headerClasses}>
            <div className={contentClasses}>
                {/* Left slot */}
                <div className={styles.headerLeft}>
                    {onBack ? (
                        // Sub-page: show back button
                        renderBackButton()
                    ) : hasInternalMenuHandler ? (
                        // Main page with new API: show menu button
                        renderMenuButton()
                    ) : showLegacyChildren ? (
                        // Legacy support: render children (old menu button)
                        children
                    ) : (
                        // Spacer for alignment
                        <div className={styles.headerSpacer} />
                    )}
                </div>

                {/* Center: Title */}
                <h1 className={styles.headerTitle}>{title}</h1>

                {/* Right slot */}
                <div className={styles.headerRight}>
                    {onBack && hasInternalMenuHandler ? (
                        // Sub-page with new API: menu on right
                        renderMenuButton()
                    ) : onBack && showLegacyChildren ? (
                        // Sub-page with legacy: children on right
                        children
                    ) : rightAction ? (
                        // Custom right action
                        rightAction
                    ) : (
                        // Spacer for alignment
                        <div className={styles.headerSpacer} />
                    )}
                </div>
            </div>
        </header>
    );
};

export default PageHeader;
